/**
 * Configurator Redux State Module
 *
 * This module manages the state for the product configurator application, which handles:
 * - Product selection and variant configuration
 * - Option selection (size, firmness, etc.)
 * - Product upgrades and add-ons
 * - Pricing and shipping information
 * - Shopping cart integration
 *
 * State Structure:
 * - `entities`: Product data including root products, pricing, bundles, and financing
 * - `ui`: UI state including selected products, options, quantities, and modal states
 * - `shippingMethod`: Shipping method information
 */
import { PayloadAction, combineReducers, createSlice } from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { t } from "ttag";

import {
    IAddon,
    IConcreteBundle,
    IOptionValues,
    IProduct,
} from "../../models/catalogue";
import { IProductCategoryID, IProductID } from "../../models/nominals";
import {
    ISetOptionValuePayload,
    setOptionValue as updateOptionValue,
} from "../../store/reducer-utils";
import { uniqueByKey } from "../../utils/functional";
import { intersection } from "../../utils/sets";
import { sortProductsByPrice } from "../../utils/sorting";
import { defaults } from "./defaults";
import { IPLCOptionPanel, IPLCProductCategorySelector } from "./models";
import { getMostSimilarProduct } from "./product-similarity";
import {
    IReduxState,
    IRootSelectionMode,
    ISelectedCategories,
    RootSelectionMode,
} from "./reducers.interfaces";
import { getUpgradeID, selectedCategoriesSelector } from "./selectors";
import {
    loadConcreteBundles,
    loadFinancingPlans,
    loadPrice,
    loadProducts,
    loadShippingMethod,
} from "./thunks";
import { getInitialVariant } from "./utils";

// ============================================================================
// Helper Functions
// ============================================================================

const processSelectedAddons = (
    startingAddons: IAddon[],
    updates: Array<{
        productID: IProductID;
        quantity: number;
    }>,
): IAddon[] => {
    const addonMap = new Map(
        startingAddons.map((addon) => [addon.productID, addon.quantity]),
    );
    for (const update of updates) {
        if (update.quantity > 0) {
            addonMap.set(update.productID, update.quantity);
        } else {
            addonMap.delete(update.productID);
        }
    }
    return Array.from(addonMap.keys()).map((productID) => {
        return {
            productID: productID,
            quantity: addonMap.get(productID) || 1,
        };
    });
};

const buildOptionValuesForVariant = (
    rootProduct: IProduct,
    variant: IProduct,
): IOptionValues => {
    const optionCodes = rootProduct.attributes.product_options?.value || [];
    const optionValues: IOptionValues = {};
    for (const optionCode of optionCodes) {
        optionValues[optionCode] = variant.attributes[optionCode]?.value;
    }
    return optionValues;
};

const buildSelectedCategoriesForVariant = (
    variant: IProduct,
    categorySelectors?: readonly IPLCProductCategorySelector[],
): ISelectedCategories => {
    if (!categorySelectors) {
        return {};
    }
    const selectedCategories: Record<string, IProductCategoryID> = {};
    const variantCatIDs = new Set(variant.category_ids);
    for (const categorySelector of categorySelectors) {
        const selectorCatIDs = categorySelector.value.options.map(
            (opt) => opt.category.id,
        );
        const overlap = Array.from(
            intersection(variantCatIDs, new Set(selectorCatIDs)),
        );
        if (overlap.length > 0) {
            selectedCategories[categorySelector.id] = overlap[0];
        }
    }

    return selectedCategories;
};

/**
 * Drop a selected upgrade that has gone out of stock.
 *
 * Out-of-stock upgrades still render (greyed out and unselectable), so a stale
 * selection would show a checked-but-disabled checkbox and leave Add to Cart
 * disabled. Only an upgrade that is present *and* unavailable is cleared — one
 * absent from the newly loaded bundles is left alone, since it is inert until
 * its bundle comes back.
 */
const resolveSelectedUpgrade = (
    selectedUpgradeID: IProductID | null,
    concreteBundles: readonly IConcreteBundle[],
): IProductID | null => {
    if (!selectedUpgradeID) {
        return null;
    }
    const isUnavailable = concreteBundles.some((bundle) =>
        bundle.suggested_products.some(
            (product) =>
                getUpgradeID(product) === selectedUpgradeID &&
                !product.availability.is_available_to_buy,
        ),
    );
    return isUnavailable ? null : selectedUpgradeID;
};

const buildPreviousSelectionState = (
    state: typeof defaults.ui,
): typeof defaults.ui.previousSelectionState => {
    return {
        selectedRoot: state.selectedRoot,
        optionValues: state.optionValues,
        quantity: state.quantity,
    };
};

const trackSelectionHistory = (
    oldState: typeof defaults.ui,
    newState: typeof defaults.ui,
): typeof defaults.ui => {
    // Only update the previousSelectionState if it will be different from the newSelectionState. This prevents
    // "squashing" history when a action duplicates are sent a couple times in a row.
    const newSelectionState = buildPreviousSelectionState(newState);
    const previousSelectionState = buildPreviousSelectionState(oldState);
    if (
        JSON.stringify(newSelectionState) ===
        JSON.stringify(previousSelectionState)
    ) {
        return newState;
    }
    return {
        ...newState,
        previousSelectionState: previousSelectionState,
    };
};

// ============================================================================
// Entities Slice
// ============================================================================

const entitiesSlice = createSlice({
    name: "configurator/entities",
    initialState: defaults.entities,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(loadProducts.fulfilled, (state, action) => {
            state.rootProducts = castDraft(
                sortProductsByPrice(action.payload.rootProducts),
            );
            state.categorySelectors = castDraft(
                action.payload.categorySelectors,
            );
            state.optionPanels = castDraft(action.payload.optionPanels);
            if (action.payload.productClass) {
                state.productClass = castDraft(action.payload.productClass);
            }
        });
        builder.addCase(loadPrice.pending, (state, action) => {
            state.priceError = null;
            state.lastPriceRequestId = action.meta.requestId;
        });
        builder.addCase(loadPrice.fulfilled, (state, action) => {
            if (state.lastPriceRequestId !== action.meta.requestId) return;
            state.price = castDraft(action.payload);
            state.priceError = null;
        });
        builder.addCase(loadPrice.rejected, (state, action) => {
            if (state.lastPriceRequestId !== action.meta.requestId) return;
            state.priceError = action.payload?.isStockError
                ? t`You've selected more than the available inventory for this item. Please update your quantity to continue.`
                : (action.payload?.reason ??
                  t`Unable to load pricing. Please try again.`);
        });
        builder.addCase(loadFinancingPlans.fulfilled, (state, action) => {
            state.financingPlans = action.payload;
        });
        builder.addCase(loadConcreteBundles.fulfilled, (state, action) => {
            state.concreteBundles = castDraft(
                action.payload.concreteBundles.slice().filter((bundle) => {
                    return bundle.suggested_products.length > 0;
                }),
            );
            state.upsellInfoModals = castDraft(
                uniqueByKey(
                    action.payload.upsellInfoModals.concat(
                        state.upsellInfoModals,
                    ),
                    (modal) => modal.id,
                ),
            );
            state.promoCards = castDraft(action.payload.promoCards);
        });
    },
});

// ============================================================================
// UI Slice
// ============================================================================

const uiSlice = createSlice({
    name: "configurator/ui",
    initialState: defaults.ui,
    reducers: {
        setSelectedRootProduct: (state, action: PayloadAction<IProduct>) => {
            const { rootProduct, variant } = getInitialVariant([
                action.payload,
            ]);
            const optionValues = buildOptionValuesForVariant(
                rootProduct,
                variant,
            );
            state.selectedRoot = {
                type: RootSelectionMode.DIRECT,
                productID: rootProduct.id,
            };
            state.optionValues = castDraft(optionValues);
        },

        setSelectedCategory: (
            state,
            action: PayloadAction<{
                selectorID: string;
                categoryID: IProductCategoryID;
                categorySelectors: readonly IPLCProductCategorySelector[];
                optionPanels: readonly IPLCOptionPanel[];
            }>,
        ) => {
            const prevSelection =
                state.selectedRoot?.type === RootSelectionMode.CATEGORY
                    ? state.selectedRoot.categories
                    : {};
            state.selectedRoot = {
                type: RootSelectionMode.CATEGORY,
                categories: {
                    ...prevSelection,
                    [action.payload.selectorID]: action.payload.categoryID,
                },
            };
        },

        setSelectedVariant: (
            state,
            action: PayloadAction<{
                root: IProduct;
                variant: IProduct;
                categorySelectors?: readonly IPLCProductCategorySelector[];
            }>,
        ) => {
            const optionValues = buildOptionValuesForVariant(
                action.payload.root,
                action.payload.variant,
            );
            const selectedRoot: IRootSelectionMode = action.payload
                .categorySelectors
                ? {
                      type: RootSelectionMode.CATEGORY,
                      categories: buildSelectedCategoriesForVariant(
                          action.payload.variant,
                          action.payload.categorySelectors,
                      ),
                  }
                : {
                      type: RootSelectionMode.DIRECT,
                      productID: action.payload.root.id,
                  };
            state.selectedRoot = selectedRoot;
            state.optionValues = castDraft(optionValues);
        },

        setOptionValue: (
            state,
            action: PayloadAction<ISetOptionValuePayload>,
        ) => {
            state.optionValues = castDraft(
                updateOptionValue(state.optionValues, action.payload),
            );
        },

        setQuantity: (state, action: PayloadAction<number>) => {
            state.quantity = action.payload;
        },

        undoSelection: (state) => {
            if (state.previousSelectionState) {
                Object.assign(state, state.previousSelectionState);
            }
        },

        setSelectedUpgrade: (
            state,
            action: PayloadAction<IReduxState["ui"]["selectedUpgrade"]>,
        ) => {
            state.selectedUpgrade = action.payload;
        },

        updateSelectedAddons: (
            state,
            action: PayloadAction<
                {
                    productID: IProductID;
                    quantity: number;
                }[]
            >,
        ) => {
            state.selectedAddons = processSelectedAddons(
                state.selectedAddons,
                action.payload,
            );
        },

        showUpsellInfoModal: (
            state,
            action: PayloadAction<{ bundleID: number }>,
        ) => {
            state.upsellInfoModalActiveBundle = action.payload.bundleID;
        },

        hideUpsellInfoModal: (state) => {
            state.upsellInfoModalActiveBundle = null;
        },

        setAddToBasketBtnState: (
            state,
            action: PayloadAction<{ cooldownActive: boolean; text: string }>,
        ) => {
            state.addToBasketCooldownActive = action.payload.cooldownActive;
            state.addToBasketButtonText = action.payload.text;
        },

        setAddToBasketErrorModalState: (
            state,
            action: PayloadAction<{ isOpen: boolean; reason: string }>,
        ) => {
            state.addToBasketErrorOpen = action.payload.isOpen;
            state.addToBasketErrorReason = action.payload.reason;
        },

        setRatingsModalState: (
            state,
            action: PayloadAction<{ isOpen: boolean }>,
        ) => {
            state.ratingsModalOpen = action.payload.isOpen;
        },

        setOptionSelectionErrorState: (
            state,
            action: PayloadAction<{ reason: string }>,
        ) => {
            state.optionSelectionErrorReason = action.payload.reason;
        },

        setPanelTriggerState: (
            state,
            action: PayloadAction<{ isClicked: boolean }>,
        ) => {
            state.panelTriggerClicked = action.payload.isClicked;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadProducts.fulfilled, (state, action) => {
            const { rootProduct, variant } = getInitialVariant(
                action.payload.rootProducts,
                action.payload.initialVariantID,
            );
            const optionValues = buildOptionValuesForVariant(
                rootProduct,
                variant,
            );
            const selectedCategories = buildSelectedCategoriesForVariant(
                variant,
                action.payload.categorySelectors,
            );
            const selectedRoot: IRootSelectionMode = action.payload
                .categorySelectors
                ? {
                      type: RootSelectionMode.CATEGORY,
                      categories: selectedCategories,
                  }
                : {
                      type: RootSelectionMode.DIRECT,
                      productID: rootProduct.id,
                  };

            state.selectedRoot = selectedRoot;
            state.optionValues = castDraft(optionValues);
        });
        builder.addCase(loadConcreteBundles.fulfilled, (state, action) => {
            state.selectedUpgrade = action.payload.selectedUpgradeID;
        });
    },
});

// ============================================================================
// Shipping Method Slice
// ============================================================================

const shippingMethodSlice = createSlice({
    name: "configurator/shippingMethod",
    initialState: defaults.shippingMethod,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(loadShippingMethod.fulfilled, (state, action) => {
            return action.payload;
        });
    },
});

// ============================================================================
// Combined Reducer with Post-Processing
// ============================================================================

const _innerReducers = combineReducers({
    entities: entitiesSlice.reducer,
    ui: uiSlice.reducer,
    shippingMethod: shippingMethodSlice.reducer,
});

/**
 * Configurator reducer with post-processing for product similarity matching and history tracking.
 *
 * IMPORTANT: This reducer includes critical post-processing logic that:
 *
 * 1. Automatically updates option values to match the most similar product when categories change
 *    - Uses getMostSimilarProduct to find the best match based on selected options
 *    - Preserves user preferences across category changes
 * 2. Tracks selection history for undo functionality
 *    - Records previous selection state before changes
 *    - Prevents duplicate history entries for repeated identical actions
 * 3. Drops a selected upgrade that is not purchasable
 *    - The only enforcement point for that invariant; see the inline comment
 *
 * This logic cannot be removed without breaking the configurator UX.
 */
export const reducers: typeof _innerReducers = (oldState, action) => {
    let newState = _innerReducers(oldState, action);

    // Out-of-stock upgrades render (greyed out) but must never become the
    // selection, or the card shows a checked-and-disabled checkbox and Add to
    // Cart locks up. Every writer of `ui.selectedUpgrade` funnels through here
    // — the card, the upsell info modal, the product-compare tile, SSR
    // `?upgrade=` hydration and the bundle load itself — because the ui slice
    // alone cannot see `entities.concreteBundles`.
    if (
        uiSlice.actions.setSelectedUpgrade.match(action) ||
        loadConcreteBundles.fulfilled.match(action)
    ) {
        const resolved = resolveSelectedUpgrade(
            newState.ui.selectedUpgrade,
            newState.entities.concreteBundles,
        );
        if (resolved !== newState.ui.selectedUpgrade) {
            newState = {
                ...newState,
                ui: { ...newState.ui, selectedUpgrade: resolved },
            };
        }
    }

    // Automatically update the option values to match the most similar product in the new category
    if (uiSlice.actions.setSelectedCategory.match(action)) {
        const match = getMostSimilarProduct({
            categorySelectors: action.payload.categorySelectors,
            optionPanels: action.payload.optionPanels,
            rootProducts: newState.entities.rootProducts,
            selectedCategories: selectedCategoriesSelector(newState),
            optionValues: newState.ui.optionValues,
            previouslySelectedCategories:
                oldState?.ui?.selectedRoot?.type === RootSelectionMode.CATEGORY
                    ? oldState.ui.selectedRoot.categories
                    : {},
            previouslySelectedOptionValues: oldState?.ui?.optionValues || {},
        });
        if (match && match.distance > 0) {
            newState = {
                ...newState,
                ui: uiSlice.reducer(
                    newState.ui,
                    uiSlice.actions.setSelectedVariant({
                        root: match.bestProduct.parent || match.bestProduct,
                        variant: match.bestProduct,
                        categorySelectors: action.payload.categorySelectors,
                    }),
                ),
            };
        }
    }

    if (oldState?.ui && oldState.ui.quantity !== newState.ui.quantity) {
        newState = {
            ...newState,
            entities: {
                ...newState.entities,
                priceError: null,
                lastPriceRequestId: null,
            },
        };
    }

    // Track the previously selected state
    if (oldState?.ui) {
        newState = {
            ...newState,
            ui: trackSelectionHistory(oldState.ui, newState.ui),
        };
    }

    return newState;
};

// ============================================================================
// Export Action Creators
// ============================================================================
export const {
    setSelectedRootProduct,
    setSelectedCategory,
    setSelectedVariant,
    setOptionValue,
    setQuantity,
    undoSelection,
    setSelectedUpgrade,
    updateSelectedAddons,
    showUpsellInfoModal,
    hideUpsellInfoModal,
    setAddToBasketBtnState,
    setAddToBasketErrorModalState,
    setRatingsModalState,
    setOptionSelectionErrorState,
    setPanelTriggerState,
} = uiSlice.actions;
